/*
 * The icon set, drawn inline.
 *
 * Inline rather than from a font or a package for two reasons: an icon font is
 * a network request that can fail and leave a sidebar of empty boxes, and
 * `currentColor` here means an icon takes the colour of whatever state its
 * row is in without a second rule anywhere.
 *
 * All on a 24-unit grid with a 1.8 stroke, so they sit at the same visual
 * weight beside 13px text.
 */

interface Props {
  size?: number;
  className?: string;
}

function svg(path: React.ReactNode, { size = 18, className }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}

export const IconDashboard = (p: Props) =>
  svg(
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>,
    p,
  );

export const IconStrategy = (p: Props) =>
  svg(
    <>
      <path d="M3 17l5-5 4 3 8-8" />
      <path d="M16 7h4v4" />
      <circle cx="8" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="15" r="1.2" fill="currentColor" stroke="none" />
    </>,
    p,
  );

export const IconPositions = (p: Props) =>
  svg(
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
      <path d="M3 12h18" />
    </>,
    p,
  );

export const IconOrders = (p: Props) =>
  svg(
    <>
      <path d="M5 3h11l4 4v14a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" />
      <path d="M15 3v5h5" />
      <path d="M8 13h8M8 17h5" />
    </>,
    p,
  );

export const IconSignals = (p: Props) =>
  svg(
    <>
      <path d="M2 12h4l3-8 4 16 3-8h6" />
    </>,
    p,
  );

export const IconChart = (p: Props) =>
  svg(
    <>
      <path d="M7 4v16M17 4v16" />
      <rect x="4.5" y="8" width="5" height="7" rx="1" />
      <rect x="14.5" y="5" width="5" height="9" rx="1" />
    </>,
    p,
  );

export const IconSettings = (p: Props) =>
  svg(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 00.33 1.77l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.6 1.6 0 00-1.77-.33 1.6 1.6 0 00-1 1.47V21a2 2 0 11-4 0v-.09A1.6 1.6 0 008.07 19.4a1.6 1.6 0 00-1.77.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.6 1.6 0 003.8 15a1.6 1.6 0 00-1.47-1H2a2 2 0 110-4h.09A1.6 1.6 0 003.6 8.93a1.6 1.6 0 00-.33-1.77l-.06-.06a2 2 0 112.83-2.83l.06.06A1.6 1.6 0 009 4.6a1.6 1.6 0 001-1.47V3a2 2 0 114 0v.09a1.6 1.6 0 001 1.47 1.6 1.6 0 001.77-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.6 1.6 0 00-.33 1.77V10a1.6 1.6 0 001.47 1H22a2 2 0 110 4h-.09a1.6 1.6 0 00-1.47 1z" />
    </>,
    p,
  );

export const IconPeople = (p: Props) =>
  svg(
    <>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </>,
    p,
  );

export const IconHelp = (p: Props) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.1 9a3 3 0 015.8 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </>,
    p,
  );

export const IconLogout = (p: Props) =>
  svg(
    <>
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>,
    p,
  );

export const IconChevron = (p: Props) => svg(<path d="M15 18l-6-6 6-6" />, p);

export const IconClose = (p: Props) => svg(<path d="M18 6L6 18M6 6l12 12" />, p);

export const IconPlus = (p: Props) => svg(<path d="M12 5v14M5 12h14" />, p);

export const IconSearch = (p: Props) =>
  svg(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.2-4.2" />
    </>,
    p,
  );

export const IconWarning = (p: Props) =>
  svg(
    <>
      <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>,
    p,
  );

export const IconCheck = (p: Props) => svg(<path d="M20 6L9 17l-5-5" />, p);

export const IconRefresh = (p: Props) =>
  svg(
    <>
      <path d="M21 2v6h-6" />
      <path d="M3 12a9 9 0 0115-6.7L21 8" />
      <path d="M3 22v-6h6" />
      <path d="M21 12a9 9 0 01-15 6.7L3 16" />
    </>,
    p,
  );

export const IconTrash = (p: Props) =>
  svg(
    <>
      <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" />
      <path d="M19 6l-1 14a1 1 0 01-1 1H7a1 1 0 01-1-1L5 6" />
      <path d="M10 11v6M14 11v6" />
    </>,
    p,
  );

export const IconPlay = (p: Props) => svg(<path d="M6 4l14 8-14 8V4z" />, p);

export const IconEdit = (p: Props) =>
  svg(
    <>
      <path d="M11 4H5a2 2 0 00-2 2v13a2 2 0 002 2h13a2 2 0 002-2v-6" />
      <path d="M18.4 2.6a2 2 0 013 3L12 15l-4 1 1-4 9.4-9.4z" />
    </>,
    p,
  );

export const IconBolt = (p: Props) => svg(<path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z" />, p);
